/* eslint-disable @typescript-eslint/no-empty-function */
import { useState } from 'react'
import styled from 'styled-components'
import { DashboardCard, P, SquareIcon } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { HrDashboardInnerWidgetContainer } from '../HrDashboard.styled'
import { WarningTriangleIcon } from 'assets/icons/WarningTriangleIcon'
import { WarningCircle } from 'assets/icons/WarningCircle'
import { EditIcon } from 'assets/icons/EditIcon'

type Severity = 'High' | 'Med' | 'Low'

type Alert = {
  id: number
  icon: React.ReactNode
  iconColor: string
  title: string
  description: string
  severity: Severity
}

const SEV_COLORS: Record<Severity, string> = {
  High: '#FF4F46',
  Med: '#ff9900',
  Low: '#38bdf8',
}

const ALERTS: Alert[] = [
  {
    id: 1,
    icon: WarningTriangleIcon,
    iconColor: '#FF4F46',
    title: '3 Unrecorded Attendance',
    description: 'Mining Ops — shift started 2h ago, 3 workers unmarked',
    severity: 'High',
  },
  {
    id: 2,
    icon: WarningCircle,
    iconColor: '#FF4F46',
    title: 'Performance Drop Alert',
    description: 'Haulage dept. attendance dropped below 80% threshold',
    severity: 'High',
  },
  {
    id: 3,
    icon: (
      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
        <path d='M15.625 2.5H4.375A1.875 1.875 0 0 0 2.5 4.375v11.25A1.875 1.875 0 0 0 4.375 17.5h11.25a1.875 1.875 0 0 0 1.875-1.875V4.375A1.875 1.875 0 0 0 15.625 2.5Zm.625 13.125a.625.625 0 0 1-.625.625H4.375a.625.625 0 0 1-.625-.625V4.375a.625.625 0 0 1 .625-.625h11.25a.625.625 0 0 1 .625.625v11.25ZM13.125 6.875H6.875a.625.625 0 1 0 0 1.25h6.25a.625.625 0 1 0 0-1.25Zm0 3.125H6.875a.625.625 0 1 0 0 1.25h6.25a.625.625 0 1 0 0-1.25Zm-3.125 3.125H6.875a.625.625 0 1 0 0 1.25H10a.625.625 0 1 0 0-1.25Z' />
      </svg>
    ),
    iconColor: '#ff9900',
    title: 'Leave Request Pending',
    description: 'Ethan Walker requests leave from Apr 5–9',
    severity: 'Med',
  },
  {
    id: 4,
    icon: <EditIcon />,
    iconColor: '#38bdf8',
    title: 'Contract Expiring',
    description: 'Francis Boakyo contract expires in 14 days',
    severity: 'Low',
  },
]

type Filter = 'All' | Severity

/* ── Styled ── */

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`

const FilterBtn = styled.button<{ $active: boolean; $color?: string }>`
  background: ${(p) => (p.$active ? (p.$color || '#ff9900') : 'transparent')};
  border: 1.5px solid ${(p) => (p.$active ? (p.$color || '#ff9900') : 'transparent')};
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${(p) => (p.$active ? '#fff' : p.theme.greyText)};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${(p) => p.$color || '#ff9900'};
    color: ${(p) => p.$color || '#ff9900'};
  }
`

const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`

const AlertRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.thinSeparator};

  &:last-child {
    border-bottom: none;
  }
`

const AlertBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const AlertTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const AlertTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textColor};
`

const SevBadge = styled.span<{ $color: string }>`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${(p) => p.$color};
  border: 1.5px solid ${(p) => p.$color};
  border-radius: 20px;
  padding: 1px 8px;
`

const AlertDesc = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.greyText};
  line-height: 1.4;
`

const EmptyText = styled.p`
  text-align: center;
  width: 100%;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.greyText};
  padding: 1rem 0;
`

/* ── Component ── */

export const HrAlertsAndSuggestionsWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const [filter, setFilter] = useState<Filter>('All')

  const filtered = filter === 'All' ? ALERTS : ALERTS.filter((a) => a.severity === filter)

  const filters: { label: Filter; color?: string }[] = [
    { label: 'All', color: '#ff9900' },
    { label: 'High', color: SEV_COLORS.High },
    { label: 'Med', color: SEV_COLORS.Med },
    { label: 'Low', color: SEV_COLORS.Low },
  ]

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => onDragStart('alerts', 2)}
      onDrop={onDragDrop}
      onDragEnd={(e) => e.preventDefault()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('alerts')
      }}
      width='1fr'
      style={{ gridArea: 'alerts', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('alerts')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}

      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0 }}>
          ALERTS & REQUESTS
        </TitleText>
        <FilterRow>
          {filters.map((f) => (
            <FilterBtn
              key={f.label}
              $active={filter === f.label}
              $color={f.color}
              onClick={() => setFilter(f.label)}
            >
              {f.label}
            </FilterBtn>
          ))}
        </FilterRow>
      </CardHeader>

      <HrDashboardInnerWidgetContainer padding='0' style={{ height: '100%' }} overflow>
        {filtered.length ? (
          <AlertsList>
            {filtered.map((alert) => (
              <AlertRow key={alert.id}>
                <SquareIcon color={alert.iconColor} height='34px' style={{ flexShrink: 0 }}>
                  {alert.icon}
                </SquareIcon>
                <AlertBody>
                  <AlertTitleRow>
                    <AlertTitle>{alert.title}</AlertTitle>
                    <SevBadge $color={SEV_COLORS[alert.severity]}>{alert.severity}</SevBadge>
                  </AlertTitleRow>
                  <AlertDesc>{alert.description}</AlertDesc>
                </AlertBody>
              </AlertRow>
            ))}
          </AlertsList>
        ) : (
          <EmptyText>No alerts</EmptyText>
        )}
      </HrDashboardInnerWidgetContainer>
    </DashboardCard>
  )
}
